import React from 'react';
import EditorialBody from 'components/EditorialBody';
import Image from 'components/NewsImage';
import Head from 'components/Head';
import { formatNewsDate } from 'utils/shared/newsDate';
import Link from 'components/Link';
import TopNav from 'components/TopNavThin';
import { Card } from 'screens/News/ui/card';
import useViewer from 'hooks/useViewer';
import { NEWS_INDEX_PAGE } from 'constants/pages';
import { FaArrowLeft } from 'react-icons/fa6';
import { AuthStatus } from 'constants/auth';
import YouMayLikeSection from './YouMayLike';
import NewsLetterForm from 'components/Website/NewsLetter/Form';
import KeyTakeaways from './KeyTakeaways';
import type { NewsDetailProps } from './types';

export default function NewsDetailComponent({
  data,
  recentArticles = [],
  jsonLd,
}: NewsDetailProps) {
  const viewer = useViewer();
  const isUserSession = viewer.status === AuthStatus.User;

  if (!data || !data.markdocBody) {
    return null;
  }

  // Calculate read time from article content
  const readTime = `${Math.max(1, Math.ceil(data.markdocBody.split(/\s+/).length / 225))} min read`;

  return (
    <>
      <Head
        title={data?.title || ''}
        description={data?.seoMeta?.metaDescription || ''}
        ogType="article"
        useCurrentUrl
      />
      <>
        {!isUserSession && <TopNav />}
        <div className="min-h-screen">
          <div className="mx-auto w-full px-2 md:px-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
              <div className="lg:col-span-2">
                <article className="prose prose-stone max-w-none pt-6">
                  <div className="">
                    <h1 className="mb-4 text-3xl font-bold text-transparent text-white sm:text-4xl md:text-5xl lg:text-6xl">
                      {data?.title}
                    </h1>
                    {data?.seoMeta && (
                      <p className="mb-2 text-lg text-gray-400">
                        {data.seoMeta?.metaDescription ?? ''}
                      </p>
                    )}
                    <div className="mb-1 flex items-center justify-between">
                      <div className="rounded-full  py-1 text-sm text-white/80 shadow-sm">
                        <div className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-3 py-1 text-sm text-white shadow-sm">
                          <span>
                            {formatNewsDate(data?.eventDate ?? data?._createdAt ?? '1970-01-01')}
                          </span>
                          {!!readTime && (
                            <>
                              <span>·</span>
                              <span>{readTime}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <Link
                        href={NEWS_INDEX_PAGE}
                        className="inline-flex items-center gap-2 text-sm text-gray-300 transition-colors visited:text-gray-300 hover:text-white"
                      >
                        <FaArrowLeft className="h-3 w-3" />
                        View all news articles
                      </Link>
                    </div>
                    {data.featuredImageUrl && (
                      <div className="relative z-10">
                        <div className="relative mx-auto h-[300px] overflow-hidden rounded-xl sm:h-[400px] md:h-[500px] lg:h-[610px]">
                          <Image
                            src={data.featuredImageUrl!}
                            alt={data.featuredImageAlt || ''}
                            fill
                            priority
                            className="overflow-hidden rounded-xl object-cover object-center"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 66vw, 800px"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="prose prose-lg mt-4 max-w-none">
                    {data.keyTakeaways && (
                      <div className="relative mb-8 overflow-hidden rounded-r-lg border-l-4 border-brand-primary bg-palette-black-2 px-6 shadow-lg">
                        <h4 className="mb-4 text-lg font-bold tracking-wide text-white">
                          KEY TAKEAWAYS
                        </h4>
                        <div className="text-sm leading-relaxed  text-gray-400 md:text-lg">
                          <KeyTakeaways content={data.keyTakeaways} variant="desktop" />
                        </div>
                      </div>
                    )}
                    {!isUserSession && (
                      <div className="my-8 flex justify-center">
                        <Card className="inline-block overflow-hidden border-0 shadow-lg">
                          <div className="relative overflow-hidden rounded-2xl border-4 border-brand-primary bg-palette-black-4 px-6 py-8 text-center shadow-2xl sm:px-8 sm:py-10">
                            <h4 className="text-base font-semibold tracking-tight !text-brand-green-primary">
                              HIGH POTENTIAL TRADES SENT DIRECTLY TO YOUR INBOX
                            </h4>
                            <p className="mt-2 text-sm font-bold leading-6 text-gray-300">
                              Add your email to receive our free daily newsletter. No spam,
                              unsubscribe anytime.
                            </p>
                            <div className="mt-4">
                              <NewsLetterForm />
                            </div>
                          </div>
                        </Card>
                      </div>
                    )}
                    <EditorialBody
                      body={data.markdocBody}
                      baseUrl={process.env.APP_URL || 'https://www.insiderfinance.io'}
                    />
                  </div>
                </article>
              </div>
              <div className="lg:block">
                <div className="sticky top-5 flex flex-col gap-2">
                  {!isUserSession && (
                    <div className="my-2 flex justify-center">
                      <Card className="inline-block overflow-hidden border-0 shadow-lg">
                        <div className="relative overflow-hidden rounded-2xl border-4 border-brand-primary bg-palette-black-4 px-6 py-8 text-center shadow-2xl sm:px-8 sm:py-10">
                          <h4 className="text-base font-semibold tracking-tight !text-brand-green-primary">
                            HIGH POTENTIAL TRADES SENT DIRECTLY TO YOUR INBOX
                          </h4>
                          <p className="mt-2 text-sm font-bold leading-6 text-gray-300">
                            Add your email to receive our free daily newsletter. No spam,
                            unsubscribe anytime.
                          </p>
                          <div className="mt-4">
                            <NewsLetterForm />
                          </div>
                        </div>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {recentArticles.length > 0 && (
            <section className="py-8 sm:py-12">
              <div className="container mx-auto">
                <YouMayLikeSection heading="Read other top news stories" data={recentArticles} />
              </div>
            </section>
          )}
        </div>
      </>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
}
